import * as React from "react";
import {
  Outlet,
  createRootRouteWithContext,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient } from "@tanstack/react-query";
import { env } from "@/env";
import { RootErrorComponent } from "@/components/root-error";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum, RumErrorEvent } from "@datadog/browser-rum";
import { PostHogProvider } from "posthog-js/react";

import { DATADOG_SERVICE, DATADOG_SITE, datadogEnvironment } from "@/lib/utils";

interface MyRouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  errorComponent: (props) => <RootErrorComponent errorProps={props} />,
  notFoundComponent: () => <NotFoundComponent />,
});

/**
 * If using the legact dashboard /dashboard, redirect to /payments/payin of the new dashboard default route
 */
const NotFoundComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.pathname === "/dashboard") {
      navigate({ to: "/payments/payin" });
    }
  }, [location]);
  if (location.pathname === "/dashboard") {
    return null;
  }

  return <div>Not found</div>;
};

const isLocalhost = window.location.hostname === "localhost";

const TanStackRouterDevtools =
  env.VITE_VOLUME_URL === "https://api.volumepay.io" ||
  env.VITE_VOLUME_URL === "https://api.sandbox.volumepay.io" ||
  env.VITE_IS_DEMO
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/router-devtools").then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      );

const ReactQueryDevtools =
  env.VITE_VOLUME_URL === "https://api.volumepay.io" ||
  env.VITE_VOLUME_URL === "https://api.sandbox.volumepay.io" ||
  env.VITE_IS_DEMO
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/react-query-devtools").then((res) => ({
          default: res.ReactQueryDevtools,
        })),
      );

if (!isLocalhost) {
  if (import.meta.env.VITE_IS_PLAYWRIGHT !== "true") {
    datadogLogs.init({
      clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: DATADOG_SERVICE,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      env: datadogEnvironment(),
      beforeSend(event) {
        if (
          event.message.includes(
            "ResizeObserver loop completed with undelivered notifications",
          ) ||
          event.error?.message?.includes(
            "ResizeObserver loop completed with undelivered notifications",
          ) ||
          event.message.includes("ResizeObserver") ||
          event.error?.stack?.includes("ResizeObserver") ||
          event.error?.message?.includes("ResizeObserver")
        ) {
          return false;
        }
        return true;
      },
    });

    datadogRum.init({
      applicationId: env.VITE_DATADOG_RUM_APPLICATION_ID,
      clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: DATADOG_SERVICE,
      env: datadogEnvironment(),
      allowedTracingUrls: [/https:\/\/.*\.volumepay\.io/],
      version: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ?? "local",
      sessionSampleRate: 100,
      sessionReplaySampleRate: env.VITE_IS_LOCAL ? 0 : 20,
      trackUserInteractions: !env.VITE_IS_LOCAL,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      beforeSend(event) {
        if (event.error) {
          const errorEvent = event as RumErrorEvent;
          if (
            errorEvent.error.message.includes("ResizeObserver") ||
            errorEvent.error.causes?.some((c) =>
              c.message.includes("ResizeObserver"),
            )
          ) {
            return false;
          }
        }
        return true;
      },
    });
  }
}

function RootComponent() {
  return (
    <PostHogProvider
      apiKey={env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: "2025-05-24",
        loaded: function (ph) {
          if (!import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA) {
            ph.opt_out_capturing(); // opts a user out of event capture
          }
        },
        autocapture: false,
        capture_pageleave: false,
      }}
    >
      <Toaster richColors />
      <Outlet />
      <ReactQueryDevtools initialIsOpen={false} />
      <TanStackRouterDevtools position="bottom-left" />
    </PostHogProvider>
  );
}
